import React, { useState, useCallback } from "react";
import DealsMix from "./DealsMix";
import FundingSummary from "./FundingSummary";
import ChartsSection from "../components/ChartsSection";
import CashFlowProjection from "../components/CashFlowProjection";
import Disclaimer from "./Disclaimer";
import { Tooltip } from "react-tooltip";
import { usePDF } from "react-to-pdf";
import SendPdf from "./SendPdf";
import vennreLogo from "../assets/vennre_logo.svg";
import helpIcon from "../assets/help-icon.svg";

const InvestmentTracker = ({ deals, mainData }) => {
  var isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const { toPDF, targetRef } = usePDF({
    method: "build",
    filename: `The V Simulator Report_${new Date()
      .toISOString()
      .slice(0, 10)}.pdf`,
    resolution: isSafari ? 1 : 3,
  });

  const [isSendPdfOpen, setIsSendPdfOpen] = useState(false);
  const showSendPdf = () => {
    setIsSendPdfOpen(true);
  };
  const hideSendPdf = () => {
    setIsSendPdfOpen(false);
  };

  const [showDisclaimer, setShowDisclaimer] = useState(() => {
    return localStorage.getItem("disclaimerAccepted") !== "true";
  });

  const [investments, setInvestments] = useState([]);
  const [cfs, setCfs] = useState([]);
  const [capital, setCapital] = useState({});

  const handleUpdateInvestments = useCallback((updatedInvestments) => {
    setInvestments(updatedInvestments);
  }, []);
  const handleUpdateCfs = useCallback((cfs) => {
    setCfs(cfs);
  }, []);

  const handleUpdateCapital = useCallback((capital) => {
    setCapital(capital);
  }, []);

  const handleAccept = () => {
    setShowDisclaimer(false);
    localStorage.setItem("disclaimerAccepted", "true");
  };

  const share = () => {
    if (navigator.share) {
      navigator
        .share({
          title:
            "Unlock Your Wealth Potential: Explore Vennre's Deals Simulator",
          text: "Hi! Thought you might find this page interesting and useful. It's The V Simulator by Vennre, for simulating deals and forecasting returns. It's a great tool for wealth building goals! Check it out.",
          url: window.location.href,
        })
        .then(() => console.log("Successful share"))
        .catch((error) => console.log("Error sharing:", error));
    }
  };

  if (!deals || !mainData) {
    return null;
  }

  return (
    <div ref={targetRef} className="w-full">
      <div className="min-h-screen container mx-auto font-sans">
        <header className="flex flex-col items-start py-8 px-6 lg:py-8 sm:px-0 gap-8">
          <a
            href="https://www.vennre.com/"
            target="_blank"
            className="flex items-center"
            rel="noreferrer"
          >
            <img src={vennreLogo} alt="Vennre Logo" className="h-6" />
          </a>
          <div className="w-full flex flex-col lg:flex-row items-baseline justify-between gap-8">
            <div className="flex items-center justify-start gap-4 w-full lg:w-auto">
              <h1 className="font-serif text-3xl font-medium text-[#4D4D4C] text-left">
                The V Simulator
              </h1>
              <div
                data-html2canvas-ignore
                className="cursor-pointer"
                data-tooltip-id="help-tooltip"
                data-tooltip-content="The tool is tailored to help users in visualising and managing their investments in private markets via the Vennre platform. It empowers users to construct their portfolios by assessing the potential impact of new investments on their existing ones. Additionally, it facilitates understanding of portfolio composition and cash flow projections throughout the investment lifecycle."
                data-tooltip-place="top"
              >
                <img src={helpIcon} className="h-5 mt-2" alt="help-icon" />
              </div>
            </div>
            <div
              className="flex flex-row items-center gap-4 text-vennre-blue-01 font-medium text-sm"
              data-html2canvas-ignore
            >
              <button
                data-tooltip-id="help-tooltip"
                data-tooltip-content="Export PDF"
                data-tooltip-place="top"
                className="group flex items-center justify-center p-1 hover:bg-vennre-gray-06 hover:border-[#071460] border border-[#d1d3d4] text-vennre-blue font-medium rounded-lg h-10 w-10"
                onClick={showSendPdf}
              >
                <svg
                  width="14"
                  height="16"
                  viewBox="0 0 14 16"
                  className="fill-vennre-gray-01 group-hover:fill-vennre-blue-01"
                >
                  <path d="M7.69578 2.37827V4.86957H11.8731C12.6396 4.86957 13.261 5.49184 13.261 6.25674V14.6128C13.261 15.3789 12.64 16 11.8731 16H2.12715C1.36064 16 0.739258 15.3777 0.739258 14.6128V6.25674C0.739258 5.49062 1.36021 4.86957 2.12715 4.86957H6.30448V2.37602L5.51876 3.16173C5.2496 3.4309 4.81306 3.43076 4.5395 3.1572C4.26783 2.88553 4.26943 2.44346 4.53496 2.17793L6.51164 0.201258C6.64547 0.0674199 6.82069 0.000164883 6.99668 0L7.00199 0.00207677C7.17866 0.00245366 7.35434 0.0692304 7.48636 0.201258L9.46304 2.17793C9.7322 2.4471 9.73207 2.88363 9.4585 3.1572C9.18683 3.42887 8.74477 3.42726 8.47924 3.16173L7.69578 2.37827ZM6.30448 6.26087H2.13056V14.6087H11.8697V6.26087H7.69578V9.04452C7.69578 9.42814 7.387 9.73913 7.00013 9.73913C6.61593 9.73913 6.30448 9.42783 6.30448 9.04452V6.26087ZM4.21752 4.86957V6.26087H9.78274V4.86957H4.21752ZM6.30448 4.86957H7.69578V6.26087H6.30448V4.86957Z" />
                </svg>
              </button>
              <a
                data-tooltip-id="help-tooltip"
                data-tooltip-content="Any questions?"
                data-tooltip-place="top"
                href="https://wa.me/447376452178"
                target="_blank"
                className="group flex items-center justify-center p-1 hover:bg-vennre-gray-06 hover:border-[#071460] border border-[#d1d3d4] text-vennre-blue font-medium rounded-lg h-10 w-10"
                rel="noreferrer"
              >
                <svg
                  height="14px"
                  width="14px"
                  viewBox="0 0 308 308"
                  className="fill-vennre-gray-01 group-hover:fill-vennre-blue-01"
                >
                  <g strokeWidth="0" />

                  <g strokeLinecap="round" strokeLinejoin="round" />

                  <g>
                    <g>
                      <path d="M227.904,176.981c-0.6-0.288-23.054-11.345-27.044-12.781c-1.629-0.585-3.374-1.156-5.23-1.156 c-3.032,0-5.579,1.511-7.563,4.479c-2.243,3.334-9.033,11.271-11.131,13.642c-0.274,0.313-0.648,0.687-0.872,0.687 c-0.201,0-3.676-1.431-4.728-1.888c-24.087-10.463-42.37-35.624-44.877-39.867c-0.358-0.61-0.373-0.887-0.376-0.887 c0.088-0.323,0.898-1.135,1.316-1.554c1.223-1.21,2.548-2.805,3.83-4.348c0.607-0.731,1.215-1.463,1.812-2.153 c1.86-2.164,2.688-3.844,3.648-5.79l0.503-1.011c2.344-4.657,0.342-8.587-0.305-9.856c-0.531-1.062-10.012-23.944-11.02-26.348 c-2.424-5.801-5.627-8.502-10.078-8.502c-0.413,0,0,0-1.732,0.073c-2.109,0.089-13.594,1.601-18.672,4.802 c-5.385,3.395-14.495,14.217-14.495,33.249c0,17.129,10.87,33.302,15.537,39.453c0.116,0.155,0.329,0.47,0.638,0.922 c17.873,26.102,40.154,45.446,62.741,54.469c21.745,8.686,32.042,9.69,37.896,9.69c0.001,0,0.001,0,0.001,0 c2.46,0,4.429-0.193,6.166-0.364l1.102-0.105c7.512-0.666,24.02-9.22,27.775-19.655c2.958-8.219,3.738-17.199,1.77-20.458 C233.168,179.508,230.845,178.393,227.904,176.981z" />
                      <path d="M156.734,0C73.318,0,5.454,67.354,5.454,150.143c0,26.777,7.166,52.988,20.741,75.928L0.212,302.716 c-0.484,1.429-0.124,3.009,0.933,4.085C1.908,307.58,2.943,308,4,308c0.405,0,0.813-0.061,1.211-0.188l79.92-25.396 c21.87,11.685,46.588,17.853,71.604,17.853C240.143,300.27,308,232.923,308,150.143C308,67.354,240.143,0,156.734,0z M156.734,268.994c-23.539,0-46.338-6.797-65.936-19.657c-0.659-0.433-1.424-0.655-2.194-0.655c-0.407,0-0.815,0.062-1.212,0.188 l-40.035,12.726l12.924-38.129c0.418-1.234,0.209-2.595-0.561-3.647c-14.924-20.392-22.813-44.485-22.813-69.677 c0-65.543,53.754-118.867,119.826-118.867c66.064,0,119.812,53.324,119.812,118.867 C276.546,215.678,222.799,268.994,156.734,268.994z" />
                    </g>
                  </g>
                </svg>
              </a>
              <button
                data-tooltip-id="help-tooltip"
                data-tooltip-content="Share with a friend"
                data-tooltip-place="top"
                className="group flex items-center justify-center p-1 hover:bg-vennre-gray-06 hover:border-[#071460] border border-[#d1d3d4] text-vennre-blue font-medium rounded-lg h-10 w-10"
                onClick={share}
              >
                <svg
                  className="fill-vennre-gray-01 group-hover:fill-vennre-blue-01"
                  width="16"
                  height="12"
                  viewBox="0 0 16 12"
                >
                  <path d="M8.50879 3.33434V0.047374C8.50849 0.0383938 8.51084 0.0295244 8.51554 0.0218674C8.52024 0.0142103 8.52709 0.00810255 8.53523 0.00430288C8.54338 0.000503203 8.55245 -0.000821185 8.56134 0.000494108C8.57023 0.0018094 8.57854 0.00570653 8.58523 0.0117015L15.3732 5.66579C15.3785 5.67008 15.3828 5.67552 15.3857 5.68169C15.3887 5.68786 15.3902 5.69462 15.3902 5.70146C15.3902 5.7083 15.3887 5.71505 15.3857 5.72122C15.3828 5.7274 15.3785 5.73283 15.3732 5.73713L8.58523 11.3887C8.57838 11.3949 8.56982 11.3988 8.56067 11.4001C8.55152 11.4013 8.54221 11.3998 8.53395 11.3957C8.52569 11.3915 8.51887 11.385 8.51438 11.377C8.50989 11.3689 8.50794 11.3596 8.50879 11.3504V8.25713C8.50865 8.18904 8.48459 8.12315 8.44081 8.07099C8.39703 8.01882 8.33632 7.9837 8.26927 7.97176C7.35963 7.80868 4.00132 7.56662 0.760222 11.9722C0.752467 11.9831 0.741594 11.9914 0.729035 11.996C0.716476 12.0006 0.702819 12.0012 0.68986 11.9979C0.6769 11.9946 0.665246 11.9875 0.656429 11.9774C0.647613 11.9674 0.642047 11.9549 0.640465 11.9416C0.533448 10.665 0.271 3.33434 8.50879 3.33434Z" />
                </svg>
              </button>
            </div>
          </div>
        </header>
        <main className="block sm:flex items-center justify-center flex-wrap xl:grid gap-6 mb-6 xl:grid-cols-12">
          <section className="col-span-7 w-full h-full">
            <DealsMix
              deals={deals}
              mainData={mainData}
              updateInvestments={handleUpdateInvestments}
              updateCfs={handleUpdateCfs}
              updateCapital={handleUpdateCapital}
            />
          </section>
          <section className="col-span-5 w-full h-full">
            <FundingSummary capital={capital} />
          </section>
          <section className="md:col-span-6 xl:col-span-12 w-full">
            <ChartsSection deals={deals} investments={investments} />
          </section>
          <section className="md:col-span-6 xl:col-span-12 w-full">
            <CashFlowProjection cfs={cfs} />
          </section>
          <footer className="p-6 sm:p-0 text-xs text-gray-500 md:col-span-6 xl:col-span-12 w-full">
            <p>
              Cash flows calculation for all non-USD based investments is
              determined by the prevailing exchange rate at the time of closing
              of each respective investment.
            </p>
            {deals
              .filter((d) => d["Exchange rate"] !== 1)
              .map((deal, index) => (
                <p key={deal.id}>
                  ({index + 1}) {deal.Deals} is based on USD/{deal.Currency}{" "}
                  rate of approximately {deal["Exchange rate"].toFixed(4)}
                </p>
              ))}
          </footer>
          {showDisclaimer && <Disclaimer onAccept={handleAccept} />}
          {isSendPdfOpen && <SendPdf onHide={hideSendPdf} createPdf={toPDF} />}
        </main>
        <Tooltip
          id="help-tooltip"
          style={{
            backgroundColor: "#4D4D4F",
            color: "white",
            borderRadius: "4px",
            fontSize: "12px",
            maxWidth: window.innerWidth > 1024 ? "840px" : "200px",
          }}
        />
        <Tooltip
          id="vennre-tooltip"
          style={{
            backgroundColor: "#4D4D4F",
            color: "white",
            borderRadius: "4px",
            fontSize: "12px",
            maxWidth: "240px",
          }}
        />
      </div>
    </div>
  );
};

export default InvestmentTracker;
